/**
 * ProDetailContainer — page/drawer-agnostic wrapper around <ProDetail>.
 *
 * Owns the heavy lifting that used to live in ProPage:
 *   - useOrderEditing for data + edit state
 *   - handleSave with the deadline-prompt flow
 *   - ReasonNoteModal rendering when a deadline note is required
 *   - Optional beforeunload warning (page mode only)
 *   - Optional global-footer Save button (page mode) or inline save bar (drawer mode)
 *
 * Consumers:
 *   <ProDetailContainer pronumber={n} mode="page"   activeTab=… onTabChange=… />
 *   <ProDetailContainer pronumber={n} mode="drawer" onClose={…}                />
 *
 * In drawer mode, activeTab is managed internally (no URL hash coupling).
 */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import useOrderEditing, { type EditSnapshot } from '../../hooks/useOrderEditing';
import SaveBar from './SaveBar';
// Type-only (erased at build time) — no runtime cycle with the slide-over, which
// imports this container.
import type { ProDetailCreate } from './ProDetailSlideOver';
import { isCreateSentinel } from './createSentinels';
import { useProEta } from '../../hooks/useProEta';
import { useProMargin } from '../../hooks/useProMargin';
import type { ProMargin } from '../../services/proDocuments.service';
import { formatETA } from '../driver-status-monitor/etaProjection';
import type { ProjectedStatus } from '../driver-status-monitor/types';
import { useDeadlineCheck } from '../../hooks/useDeadlineCheck';
import { useToast } from '../../contexts/ToastContext';
import { useDispatchFooter } from '../../contexts/DispatchFooterContext';
import { useOrderFormContext } from '../../contexts/OrderFormContext';
import { useWorkingStatusPreference } from '../../contexts/WorkingStatusPreferenceContext';
import { buildDriverRemovalsForDeadlineCheck } from '../../utils/buildDriverRemovals';
import { getDispatchScope } from '../../utils/orderLegDiff';
import dispatchRemovalService from '../../services/dispatchRemoval.service';
import dispatchOverrideApprovalService, {
  type OverrideApprovalRecord,
} from '../../services/dispatchOverrideApproval.service';
import ReasonNoteModal from '../ReasonNoteModal';
import DeleteReasonModal from './DeleteReasonModal';
import RollingReasonModal, { RollingReasonValues } from './RollingReasonModal';
import ordersService, { type ChangeAllChangeSet } from '../../services/orders.service';
import { buildOrderChangeAllRows } from '../../utils/buildOrderDataRows';
import { buildLegChangeAllRows } from '../../utils/legChangeAll';
import { buildLegLocationChangeAllRows } from '../../pages/pro/changeAll/legLocationChangeAll';
import { proDetailPath } from '../../utils/proSearch';
import ProDetail from '../../pages/pro/ProDetail';
import { AdminOverrideProvider, useAdminOverrideState } from '../../pages/pro/AdminOverrideContext';
import type { DriverValidationSnapshot } from '../../pages/pro/components/LegsGrid';
import DriverOverrideApprovalModal, {
  type DriverOverrideRow,
} from '../../pages/pro/components/DriverOverrideApprovalModal';
import TroubleTicketEditModal from '../../pages/pro/components/TroubleTicketEditModal';
import troubleTicketsService from '../../services/troubleTickets.service';
import { isThdBillto } from '../../constants/billtosCustomers';
import WarningConfirmModal from '../ui/WarningConfirmModal';
import AlertModal from '../ui/AlertModal';

import { useCanEditHazmat } from '../../pages/pro/useCanEditHazmat';
import { useCanEditTemplateFields } from '../../pages/pro/useCanEditTemplate';
import { useCanEditOrderLcFields } from '../../pages/pro/useCanEditOrderLcFields';
import { useCanEditDriverField } from '../../pages/pro/useCanEditDriverField';
import { useCanEditOrderWeightFields } from '../../pages/pro/useCanEditOrderWeightFields';
import { useResolvedAccessLevel } from '../PermissionsContainer';
import {
  QUOTE_NUMBER_PERMISSION_ID,
  ACCESS_LEVEL_EDIT,
  ACCESS_LEVEL_EXEMPT,
} from '../../pages/pro/components/InvoicingView';
import { ChangeAllProvider, type ChangeAllState } from '../../pages/pro/changeAll/ChangeAllContext';

/** Stable identity so the flagged-fields state starts (and resets) without churn. */
const EMPTY_FLAGGED: ReadonlySet<string> = new Set<string>();
import { isTemplateApproved } from '../../pages/pro/templateFieldPolicy';
import { isBelowMarginLimit } from '../../utils/marginLimit';
import {
  buildInvoicingFanoutParts,
  mergeFanout,
  ChangeAllFanoutModal,
  type ChangeAllFanout,
} from '../../pages/pro/changeAll/fanout';

type Mode = 'page' | 'drawer';

type Props = {
  /** Numeric order pronumber, or a "T"-prefixed template pronumber ("T110").
   *  Templates render read-only (no save, order-info + addl-info tabs only). */
  pronumber: number | string;
  /** 'page' (default) renders the save button into the global DispatchFooter
   *  slot; 'drawer' renders an inline save bar at the bottom of the panel. */
  mode?: Mode;
  /** Page mode supplies activeTab from the URL hash; drawer mode omits it
   *  and the container manages a local activeTab state. */
  activeTab?: string;
  /** Drawer mode only: which tab a freshly opened PRO lands on. Defaults to
   *  order-info. A caller that opens the drawer from an invoice context passes
   *  'invoicing' so the reader arrives where the thing they clicked lives. */
  initialTab?: string;
  onTabChange?: (id: string) => void;
  /** Drawer-only: shown next to Save so the user can dismiss the panel. This is
   *  the guarded close (prompts on unsaved order-edit / invoicing changes). */
  onClose?: () => void;
  /** Drawer-only: close used after a successful save when "Auto Close On Save"
   *  is enabled. Distinct from `onClose` because the just-saved order edits are
   *  clean, but the parent's lifted dirty flag hasn't caught up yet — so this
   *  close skips the order-edit unsaved-changes prompt (invoicing still guarded).
   *  Falls back to `onClose` when not supplied. */
  onCloseAfterSave?: () => void;
  /** Drawer-only: lifts the meta info (equipment / bill-to / customer) and
   *  the move-type / key-account badges up so the drawer chrome can render
   *  them inline next to "PRO {pronumber}". */
  onBadgeInfo?: (info: {
    movetype?: string;
    classification?: string;
    /** True when the record is soft-deleted — drawer shows a red DELETED banner. */
    deleted?: boolean;
    equipmentNumber?: string;
    billTo?: { name: string; code?: string | number } | null;
    customer?: { name: string; code?: string | number } | null;
    eta?: { text: string; status: ProjectedStatus } | null;
    liveSharePro?: string;
    liveShareUrl?: string;
    liveShareExpiresAt?: string;
    /** Header pill needs pct + underLimit; `data` carries the full showmargin
     *  read-model so the pill can open the breakdown modal on click. */
    margin?: { pct: number | null; underLimit: boolean; data: ProMargin | null } | null;
    marginError?: string | null;
    /** True while the margin is still loading — drives a header "Loading…" pill. */
    marginLoading?: boolean;
    /** Re-fetch the margin (fresh pull). The header calls this when the user
     *  opens the margin breakdown modal so it reflects newly-added driver-pay
     *  line items and an up-to-date margin %. */
    marginRefresh?: () => void;
  }) => void;
  /** Drawer-only: optional content rendered on the left side of the
   *  Close/Save footer (e.g. drawer width debug). */
  footerLeft?: React.ReactNode;
  /** Drawer-only: lifts the unsaved-changes flag up so the drawer chrome can
   *  guard Master Pro navigation behind an unsaved-changes warning. */
  onDirtyChange?: (dirty: boolean) => void;
  /** Change-All mode: bulk target set (anchor = pronumber). Off when null. */
  changeAll?: { targetPros: (number | string)[] } | null;
  /** Create New Order mode (drawer only). Active while `pronumber` is the
   *  create flow's 'NEW' sentinel; the flow flips it to the allocated pro after
   *  `onCreated`, which turns every branch below back into the ordinary path. */
  create?: ProDetailCreate | null;
};

const ProDetailContainer: React.FC<Props> = ({
  pronumber,
  mode = 'page',
  activeTab: activeTabProp,
  initialTab,
  onTabChange: onTabChangeProp,
  onClose,
  onCloseAfterSave,
  onBadgeInfo,
  footerLeft,
  onDirtyChange,
  changeAll = null,
  create = null,
}) => {
  const { showToast } = useToast();
  const { setDispatchButton } = useDispatchFooter();
  const { effectiveDispatcherIsGwi, defaultSendToGwiMove } = useOrderFormContext();
  const { autoCloseProOnSave } = useWorkingStatusPreference();

  // A Template is a stripped-down order whose pronumber starts with "T"
  // (e.g. "T110"). Phase 1 reuses this view READ-ONLY: no save, no order-only
  // side fetches, and only the order-info + addl-info tabs. numericPro is the
  // numeric part, used purely to satisfy the `pronumber: number` props on the
  // order-only children below — those code paths are unreachable for templates.
  const isTemplate =
    typeof pronumber === 'string' && pronumber.trim().toUpperCase().startsWith('T');
  // What this record is called in user-facing text. Matches ProDetailSlideOver's
  // `recordLabel` so the drawer header, its prompts and these loading states all
  // say the same word for the same record.
  const recordLabel = isTemplate ? 'TEMPLATE' : 'PRO';
  // Create mode stays live only while there's no real pro yet — the same rule
  // useOrderEditing applies internally, so flipping `pronumber` to the allocated
  // number turns the whole surface back into an ordinary open pro even if the
  // host keeps passing `create`.
  // Create mode ends the moment a real record exists. Asking "is the pronumber a
  // positive NUMBER" only worked for orders: a template allocates a "T…" STRING, so
  // it never left create mode — which hid History / Whse / Addl Cost on a template
  // that had already been created, and kept the drawer reporting itself dirty.
  const createActive = !!create && isCreateSentinel(pronumber);
  const numericPro =
    typeof pronumber === 'number'
      ? pronumber
      : parseInt(String(pronumber).replace(/\D/g, ''), 10) || 0;

  // Auto-triggered REJECTED trouble ticket (port of legacy cms.js leg_movetype
  // / leg_movetype2): the editing hook fires onLegActionRejected when a leg's
  // destination Move Type (legAction2) transitions to REJECTED — directly, or
  // via a next-leg legtype edit that cascades onto the previous leg. We pop a
  // pre-set NEW ticket unless an open rejection already exists on this PRO today.
  // The nonce bump on save refreshes the Trouble Tickets
  // sub-tab badge.
  //
  // The ticket is MANDATORY (#33683). Closing the modal without saving one is still
  // allowed — a ticket create can fail on validation or a 500, and a modal with no exit
  // would strand the user on a reload that throws away every other staged edit — but it
  // no longer leaves the REJECTED behind. The hook hands us the staged edits as they
  // stood immediately BEFORE the REJECTED; reverting to them takes the Move Type back to
  // what it was, along with every cascade the edit set off. Same contract as the
  // delivery-appointment reschedule gate in LegsGrid, which reaches it by deferring its
  // write instead — see the note in useOrderEditing.updateLegField for why the widget
  // picks the mechanism.
  const [rejectedTicketOpen, setRejectedTicketOpen] = useState(false);
  const [rejectedTicketChecking, setRejectedTicketChecking] = useState(false);
  const rejectedCheckRef = useRef<AbortController | null>(null);
  // The snapshot lives in a REF, not in state, because TroubleTicketEditModal calls
  // onSaved() and THEN onClose() on the save path (TroubleTicketEditModal.tsx:674-676) —
  // the same shape RescheduleEditModal uses. So onClose cannot tell a cancel from a save
  // on its own, and a saved ticket would have its REJECTED reverted right back off. A ref
  // clears synchronously, so onSaved empties it before onClose can read it; state would
  // still hold the pre-save value inside the same batch.
  const rejectedRevertRef = useRef<EditSnapshot | null>(null);
  const [troubleTicketRefreshNonce, setTroubleTicketRefreshNonce] = useState(0);

  useEffect(() => {
    rejectedRevertRef.current = null;
    setRejectedTicketChecking(false);
    setRejectedTicketOpen(false);
    return () => {
      rejectedCheckRef.current?.abort();
      rejectedCheckRef.current = null;
    };
  }, [pronumber]);

  // Gates the Rule 6 hazmat save-block so read-only-hazmat users aren't blocked
  // on unrelated saves (legacy write-ACL parity). Same value feeds the hook's
  // save gate and OrderFormView's inline banner via useCanEditHazmat.
  const canEditHazmat = useCanEditHazmat();
  // Single CSR/LC gate for the whole template edit surface (defaultDisabled).
  // Only meaningful for templates; threaded through ProDetail into every view's
  // template canEdit branch. Non-templates ignore it.
  const canEditTemplate = useCanEditTemplateFields();
  // Ports fields (Port of Origin / Discharge) share the order-lc-fields gate;
  // it also drives the import GTG→ports hard-block inside the hook.
  const canEditPorts = useCanEditOrderLcFields();
  // The invoice-hold pair (Hold Reason + the Revised DO flag) rides that SAME grant
  // — it is InvoicingView's `holdEditable` — and gates the "Hold Reason is required
  // when Revised DO is Y" save-block. Aliased rather than re-resolved so the rule and
  // the fields can never disagree; named separately because it is a different rule
  // (see the hook's canEditInvoiceHold doc).
  const canEditInvoiceHold = canEditPorts;
  // Driver-field edit access (`leg-driver-field`). Sole gate for the
  // drop→clear-appt-dates prompt: users who can edit the driver field can
  // remove a driver assignment themselves, so they never see the prompt.
  const canEditDriverField = useCanEditDriverField();
  // Quote # requirement: enforced only when invoice-quote-number resolves to
  // exactly Edit (LCs). Exempt — also the un-granted default — waives it, so
  // DispatchManager and other order-lc-fields holders save freely.
  const quoteNumberRequired =
    useResolvedAccessLevel(
      QUOTE_NUMBER_PERMISSION_ID,
      false,
      false,
      ACCESS_LEVEL_EXEMPT,
    ) === ACCESS_LEVEL_EDIT;
  // Weight access only picks the wording of the IMPORT weight-required block
  // (the rule itself applies to everyone): no grant → "contact the LC".
  const canEditWeightFields = useCanEditOrderWeightFields();
  // Admin Override (#33501) is owned HERE rather than in ProDetail because useOrderEditing
  // — which builds the save payloads that must carry the flag — is called at this level,
  // above ProDetail's tree. ProDetail and every field consume it through the context
  // provided below. Ineligible on templates (their approve/unapprove flow is the
  // authority) and in create mode (nothing is locked yet).
  const adminOverrideRaw = useAdminOverrideState(pronumber, !isTemplate && !create);
  const editing = useOrderEditing(pronumber, {
    effectiveDispatcherIsGwi,
    defaultSendToGwiMove,
    isTemplate,
    canEditHazmat,
    canEditPorts,
    canEditInvoiceHold,
    canEditDriverField,
    quoteNumberRequired,
    canEditWeightFields,
    // Rides every save this hook issues so the write is tagged `override` in history.
    adminOverrideActive: adminOverrideRaw.active,
    onLegActionRejected: ({ revertSnapshot }) => {
      if (rejectedCheckRef.current) return;
      const controller = new AbortController();
      rejectedCheckRef.current = controller;
      rejectedRevertRef.current = revertSnapshot;
      setRejectedTicketChecking(true);
      void troubleTicketsService.hasOpenRejectionToday(numericPro, controller.signal)
        .then((exists) => {
          if (controller.signal.aborted) return;
          if (exists) {
            // A ticket already documents this PRO's rejection today. Keep the edit.
            rejectedRevertRef.current = null;
          } else {
            setRejectedTicketOpen(true);
          }
        })
        .catch(() => {
          if (controller.signal.aborted) return;
          editing.revertToSnapshot(revertSnapshot);
          rejectedRevertRef.current = null;
          showToast({ type: 'error', message: 'Could not check existing rejection tickets. Please try the rejection again.' });
        })
        .finally(() => {
          if (controller.signal.aborted) return;
          rejectedCheckRef.current = null;
          setRejectedTicketChecking(false);
        });
    },
    // #33649 — the hook retyped the order's previous DELIVERY stop to STOPOFF so
    // this one could take its place. Announce it: the demoted leg is typically
    // paid and locked, its cells are greyed, and it may be scrolled out of view —
    // the write is deliberate, but it must not be invisible. There is no undo;
    // backing out means closing the pro without saving.
    onDeliveryDemoted: ({ legnums }) =>
      showToast({
        type: 'info',
        message:
          `Leg${legnums.length > 1 ? 's' : ''} ${legnums.join(', ')} changed from `
          + 'DELIVERY to STOPOFF — an order can only have one delivery stop.',
        duration: 8000,
      }),
    createMode: create
      ? {
          seed: create.seed,
          spec: create.spec,
          notes: create.stagedNotes,
          stagedDocTypes: create.stagedDocs.map((d) => d.documenttype),
          templateDocTypes: create.templateDocTypes,
        }
      : undefined,
  });
  // ── Admin Override: turning it OFF undoes what it enabled ────────────────────
  // Snapshot the staged edits the moment override goes on, so switching it off can undo
  // exactly the edits it permitted — and leave alone anything staged beforehand, which
  // the user never agreed to lose. Found in testing: without this, switching off
  // re-locked the fields but left the edits sitting in them, unreachable.
  const overrideSnapRef = useRef<EditSnapshot | null>(null);
  const [confirmOverrideOff, setConfirmOverrideOff] = useState(0); // 0 = closed, else count
  useEffect(() => {
    if (adminOverrideRaw.active) overrideSnapRef.current = editing.snapshotEdits();
    else overrideSnapRef.current = null;
    // snapshotEdits is stable; re-snapshotting on every edit would defeat the purpose.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminOverrideRaw.active]);

  // The value handed to the tree: identical to the raw state except that switching OFF
  // with override-era edits pending asks first instead of silently stranding them.
  const adminOverride = useMemo(() => ({
    ...adminOverrideRaw,
    setActive: (on: boolean) => {
      if (!on && adminOverrideRaw.active) {
        const n = editing.countEditsSince(overrideSnapRef.current);
        if (n > 0) { setConfirmOverrideOff(n); return; }
      }
      adminOverrideRaw.setActive(on);
    },
  }), [adminOverrideRaw, editing]);

  const discardOverrideEdits = useCallback(() => {
    editing.revertToSnapshot(overrideSnapRef.current);
    setConfirmOverrideOff(0);
    adminOverrideRaw.setActive(false);
  }, [editing, adminOverrideRaw]);

  const {
    checkAndPrompt,
    showNoteModal,
    handleNoteSubmit: onNoteSubmit,
    handleNoteCancel,
  } = useDeadlineCheck();

  // Driver-ETA projection for this PRO's legs. Called unconditionally (Rules of
  // Hooks) before the loading/not-found early returns below. The hook tolerates
  // NaN pronumbers (templates) and empty/undefined legs — it early-outs with no
  // fetch when there's no assigned driver, so it's cheap in every mode.
  const proEta = useProEta(numericPro, editing.effectiveLegs);

  // Live per-Pro margin (V1 showmargin read-model). Drives the header Margin
  // pill (drawer) and the per-leg Pay/Margin cells in LegsGrid. Like useProEta,
  // it's called unconditionally and no-ops on template/NaN pronumbers.
  const proMargin = useProMargin(numericPro);

  const [internalTab, setInternalTab] = useState(initialTab ?? 'order-info');
  // The drawer is not remounted between PROs — it swaps `pronumber` in place — so the
  // landing tab has to be re-applied per PRO, or only the first one opened would honour
  // it. Keyed on the PRO so a tab the reader switches to mid-visit is left alone.
  const landedFor = useRef<number | string | null>(null);
  useEffect(() => {
    if (!initialTab || pronumber == null || landedFor.current === pronumber) return;
    landedFor.current = pronumber;
    setInternalTab(initialTab);
  }, [initialTab, pronumber]);
  const activeTab = activeTabProp ?? internalTab;

  // Tab switches keep ALL pending edits — the editing hook lives at this
  // container level, so editedOrderFields / editedLegs AND the Invoicing tab's
  // line-item draft (mirrored into the hook) survive across child unmounts, and
  // a single footer Save bundles everything together. No per-tab guard needed.
  const setActiveTab = (id: string) => {
    if (onTabChangeProp) {
      onTabChangeProp(id);
    } else {
      setInternalTab(id);
    }
  };

  // Page mode: warn on full-page nav (close tab / refresh) when there are
  // unsaved edits. Drawer mode skips this — closing the drawer doesn't
  // unload the page, so a beforeunload prompt would be confusing.
  useEffect(() => {
    if (mode !== 'page' || !editing.hasChangesIgnoringAutoStagedSend) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [mode, editing.hasChangesIgnoringAutoStagedSend]);

  // The save button (in either mode) needs the latest handleSave. The
  // page-mode setDispatchButton effect can't list every editing dep, so
  // without a ref it would capture a stale handleSave and re-run prior
  // validation against current state.
  // Resolves to `true` when it's safe to proceed (the order saved, or there
  // was nothing to save) and `false` when the save was blocked/cancelled
  // (validation, stale conflict, deadline/override cancel, network error).
  // The Reuse / Undo Reuse actions use this to enforce save-before-reuse: in
  // the legacy CMS, "Reuse Container" was a submit button on the order edit
  // form, so the order saved first (and a failed save aborted the reuse). The
  // plain Save button ignores the returned value.
  const handleSaveRef = useRef<() => Promise<boolean>>(() =>
    Promise.resolve(false),
  );

  // Mirror of LegsGrid's per-leg driver validation results. The grid writes
  // to this ref imperatively on every render so click-time save handlers
  // can synchronously read the latest snapshot without a re-render race.
  const driverValidationSnapshotRef = useRef<DriverValidationSnapshot>({});

  // Override-approval modal state. When non-null, the modal is rendered;
  // resolve()/reject() drive the awaited promise in handleSave so the
  // caller can pause until the user picks Save-with-Override or Cancel.
  const [overridePrompt, setOverridePrompt] = useState<{
    rows: DriverOverrideRow[];
    resolve: (reason: string | null) => void;
  } | null>(null);

  // Chassis-provider confirm prompt. Same resolve()-driven pattern as
  // overridePrompt: handleSave awaits the promise while the WarningConfirmModal
  // is shown, so the save pauses until the user confirms or cancels.
  const [chassisConfirm, setChassisConfirm] = useState<{
    message: string;
    resolve: (ok: boolean) => void;
  } | null>(null);

  // Native-mode duplicate-order confirm. Same resolve()-driven pattern: the
  // save() hook awaits confirmDuplicate() while this WarningConfirmModal is shown,
  // then resends with bypassDupeCheck when the user picks "Save Anyway".
  const [dupeConfirm, setDupeConfirm] = useState<{
    dupes: Array<string | number>;
    resolve: (ok: boolean) => void;
  } | null>(null);
  const confirmDuplicate = useCallback(
    (dupes: Array<string | number>) =>
      new Promise<boolean>((resolve) => setDupeConfirm({ dupes, resolve })),
    [],
  );

  // Invoicing field-9 SOFT confirm: missing Bill To Ref# on Pending Approval →
  // RESOLVED. Same resolve()-driven WarningConfirmModal pattern as dupeConfirm.
  const [billToRefConfirm, setBillToRefConfirm] = useState<{
    resolve: (ok: boolean) => void;
  } | null>(null);
  const confirmMissingBillToRef = useCallback(
    () => new Promise<boolean>((resolve) => setBillToRefConfirm({ resolve })),
    [],
  );

  // Create-only advisory confirm: no Quote # and/or no invoice line items.
  // Same resolve()-driven WarningConfirmModal pattern as billToRefConfirm.
  const [createWarnConfirm, setCreateWarnConfirm] = useState<{
    warnings: string[];
    resolve: (ok: boolean) => void;
  } | null>(null);
  const confirmCreateWarnings = useCallback(
    (warnings: string[]) =>
      new Promise<boolean>((resolve) => setCreateWarnConfirm({ warnings, resolve })),
    [],
  );

  // Delete flow: flipping the Addl Info "Deleted" field to Y opens a reason modal
  // that immediately saves the delete (sr_orderdelete runs on the CMS side). Null
  // when closed; { saving } while the immediate save is in flight.
  const [deleteModal, setDeleteModal] = useState<{ saving: boolean } | null>(null);
  // True while the delete-eligibility check is in flight. Locks the Deleted field
  // and the whole save (SaveBar `busy`) so a user can't flip + save a reasonless
  // delete before the check resolves, and shows a "checking" indicator.
  const [deleteChecking, setDeleteChecking] = useState(false);
  // Set to the deleted order's billto once a Home Depot-family order has actually been
  // deleted, which opens the THD "SHIPMENT CANCELLED" ticket prompt. The partner has to
  // be told the shipment is cancelled, and that notice is an EDI event the trouble
  // ticket raises — so the delete offers the ticket rather than firing an event behind
  // the user's back. Declining is a real answer: no ticket, no event, delete stands.
  //
  // WHY A PROMPT AND NOT A SAVERULE. It was briefly built as one (v1 sr_orderdelete) and
  // pulled back out: plenty of deletes are DUPLICATES, and telling Home Depot a shipment
  // was cancelled when the pro was only a dupe of a live order is worse than telling
  // them nothing. Only the person deleting knows which it is, so the decision cannot be
  // inferred from the delete itself. Do not "simplify" this into an automatic event.
  const [cancelTicketBillTo, setCancelTicketBillTo] = useState<number | null>(null);
  // Same prompt, held back while a Change All delete is being reviewed: that modal opens
  // first and two stacked dialogs would be unreadable. Promoted to the state above once
  // the review closes, so the one ticket can then be fanned across the set with the
  // editor's own "Apply to all" (non-family pros are skipped server-side).
  const pendingCancelBillToRef = useRef<number | null>(null);

  // Rolling flow (Order Info "Rolling Fault" field): setting a fault while Booking
  // Hold is ROLLING opens the "Reason for Rolling" prompt, whose Save commits the
  // whole record (V1 `sub rolling` -> editrecordform.edit_submit). Null when closed.
  const [rollingModal, setRollingModal] = useState<
    { fault: string; saving: boolean } | null
  >(null);
  // The prompt's three answers, held exactly like V1's hidden form inputs
  // (lib_editrecord.pl:763-765): they live on the form until it's submitted, so a
  // save that gets blocked (validation, dupe confirm) and is retried still carries
  // them. Cleared on a successful save, on Cancel, and when the drawer switches PRO.
  const rollingInputsRef = useRef<RollingReasonValues | null>(null);
  // True while the prompt is open and still UNANSWERED — what blocks a save that
  // would roll the booking without the reason. A ref rather than reading
  // `rollingModal`, because the prompt's own Save clears it and calls handleSave in
  // the same tick: a state read there would still see "open and unanswered" and
  // block the very save the user just asked for.
  const rollingPromptPendingRef = useRef(false);

  // Per Diem Responsibility note the Addl Cost prompt saved this session (null = none).
  // The note is NOT an order column, so it can't ride editedOrderFields — it's held here
  // like the rolling answers and folded into the Change All ChangeSet the way
  // `deleteReason` is, so one Apply carries both the Responsible column and its note.
  // The prompt already persisted it for the ANCHOR (V1 posted saveperdieminfo on its own
  // Save), so this only feeds the fan-out.
  const perDiemNoteRef = useRef<string | null>(null);

  // Create mode: post-create work the user must wait on (staged-doc replay).
  // Non-null holds the footer locked and labels the button with the message.
  const [createBusy, setCreateBusy] = useState<string | null>(null);

  // "Copied ✓" feedback for the load-error report's copy button.
  const [errorReportCopied, setErrorReportCopied] = useState(false);

  // Recorded override approvals for this PRO. Fetched on mount and after
  // every successful save so the save-time dedupe and the inline leg-row
  // indicator both see the latest server state.
  const [overrideApprovals, setOverrideApprovals] = useState<OverrideApprovalRecord[]>([]);
  const refreshOverrideApprovals = React.useCallback(async () => {
    // Order-only lookup — skip for templates (read-only, no dispatch) and while
    // creating (no pro exists yet; numericPro would be 0).
    if (isTemplate || createActive) return;
    try {
      const data = await dispatchOverrideApprovalService.getByPro(numericPro);
      setOverrideApprovals(data);
    } catch (err) {
      console.error('Failed to load driver override approvals:', err);
    }
  }, [isTemplate, createActive, numericPro]);
  useEffect(() => {
    refreshOverrideApprovals();
  }, [refreshOverrideApprovals]);

  // Set of approved (legrecno, driver) keys, used at save-time to suppress
  // re-prompting the user for assignments that have already been justified.
  // Pending new legs (negative legrecno) won't match anything in here and
  // therefore always prompt on first save — by design, since the row only
  // earns a stable legrecno once CMS persists it.
  const approvedKeys = React.useMemo(() => {
    const set = new Set<string>();
    for (const a of overrideApprovals) {
      if (a.legrecno != null) set.add(`${a.legrecno}:${a.driver}`);
    }
    return set;
  }, [overrideApprovals]);

  // Change-All: bridge to the live ChangeAllProvider value. handleSave PROVIDES
  // the context (so it can't call useChangeAll()), so the provider keeps this ref
  // current with { checkedFields, targetPros, … }. Null when change-all is off.
  const changeAllStateRef = useRef<ChangeAllState | null>(null);
  // The ref above is enough to READ inside handleSave, but the footer has to
  // RE-RENDER when a flag is toggled: a flagged field is now savable on its own,
  // with no anchor edit behind it. Identity only changes when a checkbox moves
  // (the provider rebuilds the Set on toggle), so this re-renders on exactly
  // those events.
  const [flaggedOrderFields, setFlaggedOrderFields] =
    useState<ReadonlySet<string>>(EMPTY_FLAGGED);
  // The two leg-LOCATION flags, tracked for the same reason and separately because
  // they live in their own namespace (a leg ROLE, not an order view key).
  const [flaggedLegFields, setFlaggedLegFields] =
    useState<ReadonlySet<string>>(EMPTY_FLAGGED);
  const handleChangeAllState = useCallback((st: ChangeAllState) => {
    setFlaggedOrderFields((prev) =>
      prev === st.checkedFields ? prev : st.checkedFields,
    );
    setFlaggedLegFields((prev) =>
      prev === st.checkedLegFields ? prev : st.checkedLegFields,
    );
  }, []);
  // The anchor's CURRENT values, as the screen shows them: the fetched order with
  // the click-trace overlay on top (ProDetail layers the same overlay for display).
  // A trace writes 1st Rcv / Doc Cut / Cutoff etc. straight to the DB and reports
  // the fresh values back WITHOUT a re-fetch, so `editing.orderDetails` alone is
  // the pre-trace snapshot — blank where the trace just filled a date. The
  // flagged-but-unedited fan-out below copies from here so it sends what the user
  // is looking at, not that stale snapshot (which, matching the siblings' own
  // blanks, used to fan out as a silent no-op while the modal said "changed").
  const [tracedOverlay, setTracedOverlay] = useState<Record<string, unknown>>({});
  const anchorCurrent = useMemo(
    () => (editing.orderDetails ? { ...editing.orderDetails, ...tracedOverlay } : null),
    [editing.orderDetails, tracedOverlay],
  );

  // Change All can now be the ONLY reason to save: flagging a field the anchor
  // already holds the right value for (copy this commodity to the other 20)
  // leaves `editing.hasChanges` false, and the footer used to stay disabled — so
  // the feature looked broken for the most natural way to use it.
  //
  // Computed from the same builder the save uses, so the button enables exactly
  // when the Apply would carry something. A flagged field the anchor has no
  // value for produces no row and no enable, rather than a button that appears
  // live and then does nothing.
  const fanoutOnlyRows = useMemo(() => {
    if (!changeAll) return 0;
    const orderRows = flaggedOrderFields.size > 0
      ? buildOrderChangeAllRows(
          editing.editedOrderFields,
          flaggedOrderFields,
          anchorCurrent,
        ).length
      : 0;
    // A ticked leg location with no anchor edit behind it is the NORMAL way to use
    // this one — "every pro in this set picks up at Bayport" — so it has to enable
    // Save on its own exactly as a flagged order field does. Built with the same
    // builder the save uses, so the button enables precisely when the Apply would
    // carry something: a flag whose leg is missing on the anchor produces no row
    // and no enable, rather than a button that looks live and then does nothing.
    const legRows = flaggedLegFields.size > 0
      ? buildLegLocationChangeAllRows(
          flaggedLegFields,
          editing.effectiveLegs,
          {
            originateLeg: editing.orderDetails?.originateLeg,
            terminateLeg: editing.orderDetails?.terminateLeg,
          },
        ).length
      : 0;
    return orderRows + legRows;
  }, [
    changeAll, flaggedOrderFields, flaggedLegFields,
    editing.editedOrderFields, editing.effectiveLegs, editing.orderDetails,
    anchorCurrent,
  ]);
  // Only meaningful with somewhere to fan TO — an anchor-only set has no targets.
  const canSaveForFanoutOnly =
    fanoutOnlyRows > 0
    && !editing.hasChanges
    && (changeAll?.targetPros ?? []).some((p) => String(p) !== String(pronumber));

  // Set after a successful anchor save to open THE review/results fan-out modal. One per
  // Save, covering every arm the user flagged — order fields, whole legs, the Invoicing
  // tab's header fields and its line items. The Invoicing tab used to fan separately
  // (its own modal, its own nonce, a second apply over the same pros); that split was an
  // artifact of the tab once owning a Save, and it cost a second saverules run per pro.
  const [fanout, setFanout] = useState<ChangeAllFanout | null>(null);

  // Capture the change-all fan-out (flagged order fields + whole legs) for the target
  // set MINUS the anchor. MUST run BEFORE editing.save() — a successful save clears the
  // edit buffer. Returns null when change-all is off, nothing is flagged, or the set is
  // just the anchor. Shared by the normal save (handleSave) and the delete path
  // (handleDeleteConfirm), which is a separate save with its own reason.
  const captureChangeAllFanout = (
    anchorPro: number | string,
  ): { pros: (number | string)[]; changes: ChangeAllChangeSet } | null => {
    const caState = changeAll ? changeAllStateRef.current : null;
    if (!caState) return null;
    // `anchorCurrent` backs the flagged-but-unedited case: copying the anchor's
    // EXISTING value to the set is the whole point of flagging a field the user
    // did not touch — and "existing" must include what a click-trace just filled
    // in (it is layered over the fetched order there, see anchorCurrent).
    const orderRows = caState.checkedFields.size > 0
      ? buildOrderChangeAllRows(
          editing.editedOrderFields,
          caState.checkedFields,
          anchorCurrent,
        )
      : [];
    const legRows = caState.checkedLegs.size > 0
      ? buildLegChangeAllRows(editing.editedLegs, editing.effectiveLegs, caState.checkedLegs)
      : [];
    // The two leg LOCATION flags. A separate builder from the whole-leg one above:
    // these send a ROLE for each target pro to resolve against its own pointers,
    // and they fan the ANCHOR's current code whether or not the user edited it —
    // the same "ticking is enough to write" rule the order fields follow.
    const legLocationRows = caState.checkedLegFields.size > 0
      ? buildLegLocationChangeAllRows(
          caState.checkedLegFields,
          editing.effectiveLegs,
          {
            originateLeg: editing.orderDetails?.originateLeg,
            terminateLeg: editing.orderDetails?.terminateLeg,
          },
        )
      : [];
    const allLegRows = [...legRows, ...legLocationRows];
    const pros = caState.targetPros.filter((p) => String(p) !== String(anchorPro));
    if ((orderRows.length === 0 && allLegRows.length === 0) || pros.length === 0) return null;
    const changes: ChangeAllChangeSet = {};
    if (orderRows.length > 0) changes.order = orderRows;
    if (allLegRows.length > 0) changes.legs = allLegRows;
    // The note rides ONLY when Responsible itself is being fanned out — the prompt that
    // collected it belongs to that column, and a note applied to pros whose Responsible
    // isn't changing would be an edit nobody asked for.
    if (
      perDiemNoteRef.current !== null
      && orderRows.some((r) => r.column === 'perdiemapproval')
    ) {
      changes.perDiemNotes = perDiemNoteRef.current;
    }
    return { pros, changes };
  };

  /** Whether the Invoicing tab has its OWN pending fan-out (header fields or line items
   *  flagged "apply to all"). The tab owns that review modal; this container only has to
   *  know it's coming, so the drawer isn't auto-closed out from under it. */
  const hasInvoicingFanout = (anchorPro: number | string): boolean => {
    const caState = changeAll ? changeAllStateRef.current : null;
    if (!caState) return false;
    const flagged =
      caState.checkedInvoiceFields.size > 0 || caState.checkedInvoiceLines.size > 0;
    if (!flagged) return false;
    return caState.targetPros.some((p) => String(p) !== String(anchorPro));
  };

  const handleSave = async (): Promise<boolean> => {
    const orderDetails = editing.orderDetails;
    if (!orderDetails) return false;
    if (rejectedCheckRef.current || rejectedTicketOpen) return false;
    // Block the normal Save while a delete is mid-flow (eligibility check running
    // or the reason modal open) — the delete has its own save with the reason, and
    // this stops a reasonless delete of the optimistically-staged 'Y'.
    if (deleteChecking || deleteModal || createBusy) return false;
    // Same for the rolling prompt: no save while it's open and unanswered, so a
    // Rolling Fault can't be persisted without the reason sr_rolling notes.
    if (rollingPromptPendingRef.current) return false;

    // Chassis-provider confirm (V1 parity, orders + templates): when the chassis
    // type is set/changed to a specialty type on a non-GWI provider, confirm the
    // pairing before saving. Cancelling aborts the save.
    {
      const ct = String(editing.editedOrderFields.chassisType ?? orderDetails.chassisType ?? '');
      const cp = String(editing.editedOrderFields.chassisProvider ?? orderDetails.chassisProvider ?? '');
      const chassisChanged =
        (editing.editedOrderFields.chassisType !== undefined &&
          editing.editedOrderFields.chassisType !== orderDetails.chassisType) ||
        (editing.editedOrderFields.chassisProvider !== undefined &&
          editing.editedOrderFields.chassisProvider !== orderDetails.chassisProvider);
      if (
        chassisChanged &&
        /COMBO|TRIAXLE|SUPER LIGHT|ULTRA LIGHT/.test(ct) &&
        cp !== '' &&
        cp !== 'GWI'
      ) {
        const confirmed = await new Promise<boolean>((resolve) => {
          setChassisConfirm({
            message: `Are you sure ${cp} is provider for a ${ct} chassis?`,
            resolve,
          });
        });
        setChassisConfirm(null);
        if (!confirmed) return false;
      }
    }

    const driverRemovals = buildDriverRemovalsForDeadlineCheck({
      pronumber: orderDetails.pronumber,
      originalLegs: orderDetails.legs,
      currentLegs: editing.effectiveLegs,
      editedLegs: editing.editedLegs,
    });

    let deadlineNote: string | undefined;
    let deadlineResult: Awaited<ReturnType<typeof checkAndPrompt>> = null;
    if (driverRemovals.length > 0) {
      deadlineResult = await checkAndPrompt(driverRemovals);
      if (deadlineResult === null) return false; // User canceled — abort save.
      if (deadlineResult.note) {
        deadlineNote = [
          deadlineResult.category_name,
          deadlineResult.reason_name,
          deadlineResult.note,
        ]
          .filter(Boolean)
          .join(' - ');
      }
    }

    // Driver qualification override check, scoped to dispatch groups.
    // Non-last legs in a group don't render a driver cell of their own
    // (the last leg owns it and the value cascades), so the validation
    // result only exists on the last leg's legnum. The rule: when the
    // last-leg driver is unqualified AND any leg in the scope is
    // incomplete (any of the four arrival/depart datetimes missing), the
    // override applies to EVERY leg in that scope — one approval record
    // per leg, sharing the captured reason. Already-approved (driver,
    // legrecno) pairs drop out so we don't re-prompt for prior justifications.
    type PendingOverrideRow = DriverOverrideRow & { legrecno: number };
    const overrideRows: PendingOverrideRow[] = [];
    const seenScopes = new Set<string>();
    for (const leg of editing.effectiveLegs) {
      const scopeRecnos = getDispatchScope(leg.legrecno, editing.effectiveLegs);
      const scopeKey = String(scopeRecnos[0] ?? leg.legrecno);
      if (seenScopes.has(scopeKey)) continue;
      seenScopes.add(scopeKey);

      const scopeLegs = scopeRecnos
        .map((rn) => editing.effectiveLegs.find((l) => l.legrecno === rn))
        .filter((l): l is NonNullable<typeof l> => !!l);
      if (scopeLegs.length === 0) continue;

      const anyIncomplete = scopeLegs.some(
        (l) =>
          !l.origArrivalDateTime ||
          !l.origDepartDateTime ||
          !l.destArrivalDateTime ||
          !l.destDepartDateTime,
      );
      if (!anyIncomplete) continue;

      // The DriverSelectorCell renders only on the last leg of the scope,
      // so validation results live on that legnum. The driver value also
      // cascades — using last-leg driver gives the canonical assignment.
      const lastLeg = scopeLegs[scopeLegs.length - 1];
      const driver = (lastLeg.driver || '').trim();
      if (!driver || driver === '000') continue;
      const result = driverValidationSnapshotRef.current[lastLeg.legnum];
      if (!result || result.isValid) continue;

      // Scope-level dedupe: if any leg currently in this scope already has
      // an approval for this driver, the scope is covered — leg structure
      // can have changed since the original grant (e.g. a leg was pulled
      // into the scope via a drop-flag flip) and the prior justification
      // still applies. Per-leg records remain the audit unit; we just
      // don't write new ones for siblings of an already-approved leg.
      const scopeAlreadyApproved = scopeLegs.some((l) =>
        approvedKeys.has(`${l.legrecno}:${driver}`),
      );
      if (scopeAlreadyApproved) continue;

      for (const scopeLeg of scopeLegs) {
        overrideRows.push({
          legnum: scopeLeg.legnum,
          legrecno: scopeLeg.legrecno,
          driver,
          missing: result.missing,
        });
      }
    }

    let overrideReason: string | null = null;
    if (overrideRows.length > 0) {
      overrideReason = await new Promise<string | null>((resolve) => {
        setOverridePrompt({ rows: overrideRows, resolve });
      });
      setOverridePrompt(null);
      if (overrideReason === null) return false; // User cancelled — abort save.
    }

    // Change-All: capture the flagged-field fan-out BEFORE saving (a successful
    // save clears editing.editedOrderFields, so we can't read the edits after).
    // A delete (Deleted->Y) never reaches here — it routes through the reason modal +
    // handleDeleteConfirm, which runs its own capture on the delete path.
    const captured = captureChangeAllFanout(orderDetails.pronumber);
    // True when a bulk fan-out modal will open after a successful save. Used both
    // to trigger the modal AND to suppress "Auto Close On Save" — closing the
    // drawer would unmount the modal (rendered inside this container) before the
    // fan-out can run. The user closes the drawer after finishing the modal.
    //
    // The Invoicing tab's flags count too. Its review modal is opened by the TAB after
    // the save (off editing.saveVersion), so it needs the drawer to stay mounted just as
    // much — but this only knew about the order-field flags, so with Auto Close On Save
    // on, an Invoicing-only fan-out was closed away before it could open (bugfix #33440).
    const hasFanout = !!captured || hasInvoicingFanout(orderDetails.pronumber);

    // Fan-out ONLY: the user flagged fields the anchor already holds the right
    // values for, so there is nothing to write on the anchor. Skip its save
    // entirely and open the review modal directly.
    //
    // It cannot simply fall through: editing.save() early-outs with 'no-changes'
    // and the fan-out fires on result.ok, so the flags would vanish with no
    // explanation. Skipping is also the RIGHT outcome rather than a workaround —
    // a no-op save would stamp lastchanged and log history on an anchor the user
    // never edited.
    //
    // The invoicing arm rides along: its values already come from the anchor's
    // saved details rather than from edits, and dropping it here would silently
    // apply only part of what the user flagged.
    if (captured && !editing.hasChanges) {
      const caState = changeAll ? changeAllStateRef.current : null;
      setFanout(
        mergeFanout(
          captured,
          caState
            ? buildInvoicingFanoutParts(
                editing.orderDetails,
                caState.checkedInvoiceFields,
                caState.checkedInvoiceLines,
              )
            : null,
          caState
            ? caState.targetPros.filter((p) => String(p) !== String(orderDetails.pronumber))
            : [],
        ),
      );
      return true;
    }

    const result = await editing.save({
      deadlineNote,
      confirmDuplicate,
      confirmMissingBillToRef,
      confirmCreateWarnings,
      // Rolling prompt answers, when one was filled out this session. Prompt-only
      // inputs (not columns): sr_rolling reads them for the EXPORT ROLLING note,
      // the 'Export Rolling' history row and the GULF WINDS fault email.
      inputs: rollingInputsRef.current ? { ...rollingInputsRef.current } : undefined,
    });
    // The record committed — the "hidden form fields" are spent (V1 reloaded the
    // form after a save). A failed save leaves them staged for the retry.
    if (result.ok) rollingInputsRef.current = null;

    if (
      result.ok &&
      deadlineResult?.note &&
      deadlineResult.qualifyingDetails.length > 0
    ) {
      dispatchRemovalService
        .recordRemovals(
          deadlineResult.qualifyingDetails.map((d) => ({
            category_id: deadlineResult!.category_id!,
            reason_id: deadlineResult!.reason_id,
            notes: deadlineResult!.note!,
            pronumber: d.pronumber,
            driver: d.driver,
            dispatch_id: d.dispatch_id,
            appt_date: d.appt_date,
            deadline_type: d.deadline_type,
            deadline_date: d.deadline_date,
          })),
        )
        .catch((err) =>
          console.error('Failed to record dispatch removals:', err),
        );
    }

    if (result.ok && overrideReason !== null && overrideRows.length > 0) {
      // New legs leave handleSave with a negative legrecno (the pending-leg
      // placeholder). After save the leg has a real one — match by (legnum,
      // driver) in freshOrderDetails to recover it so the dedupe check on
      // the next save can find this approval.
      const freshLegs = result.freshOrderDetails.legs;
      const resolveLegrecno = (row: PendingOverrideRow): number | null => {
        if (row.legrecno > 0) return row.legrecno;
        const match = freshLegs.find(
          (l) => l.legnum === row.legnum && (l.driver ?? '').trim() === row.driver,
        );
        return match?.legrecno ?? null;
      };
      // Snapshot the load-side qualification inputs from the post-save order
      // state. These are the values the driver was checked against; recording
      // them on the audit row means reviewers don't have to reconstruct the
      // order's state at the moment of approval. All eight fields are stable
      // on OrderDetails and align 1:1 with the columns added in migration 188.
      const fresh = result.freshOrderDetails;
      const snapshot = {
        lbs: fresh.lbs ?? null,
        hazmat: fresh.hazmat ?? null,
        tank: fresh.isTank ?? null,
        daycab: fresh.isDaycab ?? null,
        otr: fresh.otr ?? null,
        size: fresh.size ?? null,
        equiptype: fresh.equipType ?? null,
        chassistype: fresh.chassisType ?? null,
      };
      dispatchOverrideApprovalService
        .recordApprovals(
          overrideRows.map((row) => ({
            // In create mode the pre-save record carries pronumber 0 — the audit
            // row has to name the pro the backend just allocated. Coerced because
            // a template create returns a "T…" string (#33471); that is safe here
            // for a reason worth stating, since nothing on THIS block enforces it:
            // `driver` is absent from TEMPLATE_FIELD_STATE, so it defaults to
            // state 1 (locked) and a template can never assign one — no driver,
            // no qualification override, no overrideRows, so this block does not
            // run for a template at all.
            pronumber: Number(result.createdPronumber ?? orderDetails.pronumber),
            legnum: row.legnum,
            legrecno: resolveLegrecno(row),
            driver: row.driver,
            reason: overrideReason!,
            driver_not_qualified_text:
              row.missing.length > 0 ? row.missing.join(', ') : null,
            ...snapshot,
          })),
        )
        .then(() => refreshOverrideApprovals())
        .catch((err) =>
          console.error('Failed to record driver override approvals:', err),
        );
    }

    // Create mode: the order now EXISTS (one atomic POST). Replay the staged
    // documents while the footer is held busy, then STOP — the create flow flips
    // the drawer's `pronumber` prop to the allocated pro and the existing
    // pronumber-change effects reload the record from the server as an ordinary
    // open pro (the editing hook drops create mode on its own). Nothing below
    // applies pre-flip: there is no change-all fan-out, and auto-close-on-save
    // would unmount the drawer the user is about to keep working in.
    if (result.ok && createActive && create && result.createdPronumber) {
      const createdPro = result.createdPronumber;
      showToast({
        type: 'success',
        message: `${isTemplate ? 'Template' : 'Order'} ${createdPro} created`,
        duration: 4000,
      });
      for (const m of result.userMessages ?? []) {
        showToast({
          type: m.level === 'error' ? 'error' : m.level === 'info' ? 'info' : 'warning',
          message: m.text,
          duration: 6000,
        });
      }
      setCreateBusy('Uploading documents…');
      try {
        await create.onCreated(createdPro);
      } finally {
        setCreateBusy(null);
      }
      return true;
    }

    if (result.ok) {
      showToast({ type: 'success', message: 'Changes saved', duration: 3000 });
      // Change-All: anchor saved cleanly — fan the flagged fields out to the rest
      // of the target set via the review/results modal (success path ONLY).
      // ONE fan-out: the order/leg rows captured before the save, folded together with
      // the Invoicing tab's flags read from the details the save just RETURNED (the
      // margin stamp is written during the save, so a pre-save read would fan a blank).
      const caState = changeAll ? changeAllStateRef.current : null;
      setFanout(
        mergeFanout(
          captured,
          caState
            ? buildInvoicingFanoutParts(
                result.freshOrderDetails,
                caState.checkedInvoiceFields,
                caState.checkedInvoiceLines,
              )
            : null,
          caState
            ? caState.targetPros.filter((p) => String(p) !== String(orderDetails.pronumber))
            : [],
        ),
      );
      // Native-mode post-save messages (v1 alert()): dupe/seal warnings, etc.
      // Surfaced as a toast per message, mapped by level.
      for (const m of result.userMessages ?? []) {
        showToast({
          type: m.level === 'error' ? 'error' : m.level === 'info' ? 'info' : 'warning',
          message: m.text,
          duration: 6000,
        });
      }
      // A backend leg conflict no longer lands here: it blocks the write and comes back
      // as 'stale' below, which keeps the user's edits and shows the same
      // "changed elsewhere" message as our own pre-save check. An ok result now always
      // means the whole save landed, legs included.
      // Drawer-only: Application Preferences > Pro Details > "Auto Close On
      // Save" dismisses the panel after a successful save. Page mode has no
      // panel to close, so the setting is a no-op there. Suppressed while a
      // change-all fan-out modal is pending (hasFanout) — closing the drawer
      // would unmount the modal before the bulk fan-out could run.
      if (mode === 'drawer' && autoCloseProOnSave && !hasFanout) {
        (onCloseAfterSave ?? onClose)?.();
      }
    } else if (result.type === 'no-changes' || result.type === 'aborted') {
      // no-op
    } else if (result.type === 'duplicate') {
      // User declined the "Save Anyway" duplicate confirm — nothing saved.
      showToast({
        type: 'info',
        message: 'Save cancelled — resolve the duplicate order and try again.',
        duration: 5000,
      });
    } else if (result.type === 'validation') {
      showToast({ type: 'error', message: result.errors.join('\n'), duration: 5000 });
    } else if (result.type === 'stale') {
      // Name what moved. Changed and added legs also carry an amber badge on the row
      // itself (LegsGrid), but a leg someone else DELETED has no row left to badge, so
      // this message is the only place it can be reported.
      const parts: string[] = [];
      if (result.changedLegnums.length) {
        parts.push(
          `leg${result.changedLegnums.length > 1 ? 's' : ''} `
          + result.changedLegnums.join(', ')
          + ' changed'
        );
      }
      if (result.removedLegnums.length) {
        parts.push(
          `leg${result.removedLegnums.length > 1 ? 's' : ''} `
          + result.removedLegnums.join(', ')
          + ' deleted'
        );
      }
      showToast({
        type: 'warning',
        message: parts.length
          ? `This order changed elsewhere — ${parts.join('; ')}. Review and save again.`
          : 'This order changed elsewhere. Review and save again.',
        duration: 8000,
      });
    } else if (result.type === 'error') {
      showToast({ type: 'error', message: result.error, duration: 5000 });
    }

    // "Safe to proceed" for save-before-reuse: a clean save, or nothing to
    // save. A stale/validation/error/aborted result blocks the follow-on
    // reuse so it never runs against rejected edits.
    return result.ok || result.type === 'no-changes';
  };
  handleSaveRef.current = handleSave;

  // --- Delete flow (Addl Info "Deleted" field) ---------------------------------
  // Invoked when the user flips Deleted -> Y. Templates are always deletable; for
  // orders we lazily check the legacy fv_deleted conditions first and block with a
  // reason if it can't be deleted. Eligible -> open the reason modal.
  const handleDeleteAttempt = async () => {
    // Optimistically stage 'Y' so the dropdown reflects the selection while we
    // check. SelectInput only re-syncs its displayed value when its `value` prop
    // changes, so staging (then reverting) is what makes the dropdown flip back
    // to 'N' on a block/cancel — a bare toast wouldn't move it.
    editing.updateOrderField('addlInfoDeleted', 'Y');
    if (!isTemplate) {
      // Lock the field + save while we check, so no reasonless delete can be
      // fired before the answer comes back.
      setDeleteChecking(true);
      let allowed = true;
      let reason = '';
      try {
        const elig = await ordersService.getDeleteEligibility(pronumber);
        allowed = elig.allowed;
        reason = elig.reason;
      } catch {
        // If the eligibility check itself errors, we can't confirm the order is
        // deletable — block rather than open the reason modal. Never let an
        // errored/unreachable check fall through to a delete.
        allowed = false;
        reason = 'could not verify delete eligibility — please try again';
      } finally {
        setDeleteChecking(false);
      }
      if (!allowed) {
        editing.revertOrderField('addlInfoDeleted');
        showToast({
          type: 'warning',
          message: `This order cannot be deleted: ${reason}`,
          duration: 6000,
        });
        return;
      }
    }
    setDeleteModal({ saving: false });
  };

  // Modal confirm: 'Y' is already staged, so immediately save the whole record
  // (matching the legacy form save) with the reason so sr_orderdelete can
  // note/history/email it. On any failure, revert the flag and surface the reason.
  const handleDeleteConfirm = async (reason: string) => {
    setDeleteModal({ saving: true });
    // Change-All bulk delete: capture the fan-out BEFORE saving (the save clears the
    // edit buffer). Fans out only when the user flagged "apply to all" — carrying the
    // reason so every pro's order_delete rule notes/history/emails it. Each target pro
    // is re-checked for delete-eligibility (and skipped with a reason) in the modal.
    const captured = captureChangeAllFanout(pronumber);
    // Read the billto BEFORE saving: a successful save clears the edit buffer and
    // refetches, and the order is gone from orderscurrent by then.
    const deletedBillTo = editing.orderDetails?.billTo ?? null;
    const result = await editing.save({ deletereason: reason });
    if (result.ok) {
      setDeleteModal(null);
      showToast({
        type: 'success',
        message: `${isTemplate ? 'Template' : 'Order'} ${pronumber} deleted`,
        duration: 3000,
      });
      // Offer the THD cancellation ticket. Orders only — templates carry no trouble
      // tickets — and only for the Home Depot family (831/897 -> thdlognetedi,
      // 827 -> hdslognetedi, 9025 -> cbtlognetedi; the handle is resolved server-side
      // from the order's own billto). A fanned delete queues it behind the review modal
      // instead of opening on top of it.
      if (!isTemplate && isThdBillto(deletedBillTo)) {
        if (captured) pendingCancelBillToRef.current = deletedBillTo;
        else setCancelTicketBillTo(deletedBillTo);
      }
      if (captured) {
        const isDelete = (captured.changes.order ?? []).some(
          (c) => c.column === 'deleted' && String(c.newValue).toUpperCase() === 'Y',
        );
        // A delete fans the delete alone — the Invoicing flags have nothing to say about
        // an order that is going away, and the modal's own title switches to Delete All.
        setFanout({
          pros: captured.pros,
          changes: isDelete ? { ...captured.changes, deleteReason: reason } : captured.changes,
          liLines: [],
        });
      }
    } else {
      editing.revertOrderField('addlInfoDeleted');
      setDeleteModal(null);
      if (result.type !== 'aborted' && result.type !== 'no-changes') {
        const msg =
          result.type === 'error'
            ? result.error
            : result.type === 'validation'
              ? result.errors.join('\n')
              : 'Delete failed';
        showToast({ type: 'error', message: msg, duration: 6000 });
      }
    }
  };

  // Cancel: un-stage the optimistic 'Y' so the dropdown reverts to 'N'.
  const handleDeleteCancel = () => {
    editing.revertOrderField('addlInfoDeleted');
    setDeleteModal(null);
  };

  // --- Rolling flow (Order Info "Rolling Fault" field) ------------------------
  // Port of V1 `sub rolling` (cms/lib/main.pl:12301), triggered by the Rolling
  // Fault dropdown's onchange (defs.pl:561). The fault is already staged by the
  // form; this only opens the prompt.
  const handleRollingFaultSet = (fault: string) => {
    rollingPromptPendingRef.current = true;
    setRollingModal({ fault, saving: false });
  };

  // Prompt Save: stash the three answers where handleSave picks them up, then run
  // the ordinary full save — V1's Save button set the hidden inputs and clicked
  // the edit form's submit, so every other save-time gate (validation, dupe
  // confirm, change-all fan-out) still applies.
  const handleRollingConfirm = async (values: RollingReasonValues) => {
    rollingInputsRef.current = values;
    rollingPromptPendingRef.current = false;
    setRollingModal((m) => (m ? { ...m, saving: true } : m));
    // handleSave owns the outcome toasts. On a blocked/failed save (validation,
    // declined dupe confirm, server error) the answers stay staged — it clears them
    // only on success — so the user can fix the blocker and save again without
    // re-entering them.
    await handleSaveRef.current();
    setRollingModal(null);
  };

  // Cancel: a Rolling Fault is not allowed without this information, so clear it
  // back to blank. (V1 restored the pre-edit value; blank is the same thing on the
  // normal roll-a-booking path, where the fault started empty.)
  const handleRollingCancel = () => {
    editing.updateOrderField('rollingFault', '');
    rollingInputsRef.current = null;
    rollingPromptPendingRef.current = false;
    setRollingModal(null);
  };

  // Switching PRO in the drawer is a fresh form — drop any unsaved prompt answers
  // so they can't ride a later save on a different order.
  useEffect(() => {
    rollingInputsRef.current = null;
    rollingPromptPendingRef.current = false;
    setRollingModal(null);
    perDiemNoteRef.current = null;
    // An unanswered cancellation prompt belongs to the pro that was deleted — it must
    // not reopen against whichever pro the drawer moves to next.
    setCancelTicketBillTo(null);
    pendingCancelBillToRef.current = null;
  }, [pronumber]);

  // Show the Save footer on editable tabs for both orders and templates. Template
  // editability is enforced per-field by the policy (OrderFormView's canEdit seam),
  // and the order-only pre-checks in handleSave (driver-removal deadline prompt,
  // driver-override approval) naturally no-op for templates since the driver field
  // is locked — no driver changes → no removals/overrides.
  // The save covers the whole record — edits accumulate across every tab in one
  // shared editing context — so surface the footer on all tabs once a record is
  // loaded. The Save button itself stays disabled until there are unsaved
  // changes, so it's a no-op on tabs the user is only viewing.
  const showFooter = !!editing.orderDetails;

  // Page mode: install the save button into the global SyncStatusFooter slot
  // (via DispatchFooterContext) so it doesn't stack a second sticky bar at
  // the bottom of the page. Drawer mode handles its own footer below.
  // useLayoutEffect rather than useEffect so the SaveBar re-registers
  // synchronously before paint when editing.isSaving flips — otherwise a
  // fast save can complete before the spinner state propagates through the
  // context round-trip.
  useLayoutEffect(() => {
    if (mode !== 'page') return;
    if (!showFooter) {
      setDispatchButton(null);
      return;
    }
    setDispatchButton(
      <SaveBar
        editing={editing}
        onSave={() => handleSaveRef.current()}
        onSaveBeforeAction={() => handleSaveRef.current()}
        busy={deleteChecking || rejectedTicketChecking}
        canSaveForFanoutOnly={canSaveForFanoutOnly}
      />,
    );
    return () => setDispatchButton(null);
  }, [
    mode,
    showFooter,
    editing.isSaving,
    editing.hasChanges,
    editing.saveError,
    editing.orderDetails,
    editing.streetTurnOpportunities,
    editing.refresh,
    setDispatchButton,
    deleteChecking,
    rejectedTicketChecking,
    // Without this the portaled footer keeps a stale closure and the button
    // never enables when a flag is toggled with no anchor edit.
    canSaveForFanoutOnly,
  ]);
  // Page mode never runs the create flow (it's drawer-only), so the footer above
  // stays on the default variant.

  // Drawer mode: lift move type + key-account AND the meta row up to the
  // slideover chrome so they can be shown inline next to "PRO {pronumber}".
  useEffect(() => {
    if (mode !== 'drawer' || !onBadgeInfo) return;
    const od = editing.orderDetails;
    const activeEta =
      proEta.activeLegnum != null ? proEta.byLeg.get(proEta.activeLegnum) : null;
    onBadgeInfo({
      movetype: od?.moveType || undefined,
      classification: od?.keyAccount || undefined,
      deleted: od?.addlInfoDeleted === 'Y',
      equipmentNumber: od?.container || undefined,
      billTo: od?.billToName ? { name: od.billToName, code: od.billTo } : null,
      customer: od?.customerName ? { name: od.customerName, code: od.customer } : null,
      liveSharePro: String(numericPro),
      liveShareUrl: activeEta?.liveShareUrl,
      liveShareExpiresAt: activeEta?.liveShareExpiresAt,
      eta: activeEta?.eta
        ? { text: formatETA(activeEta.eta), status: activeEta.status }
        : null,
      margin: {
        pct: proMargin.savedMarginPct ?? null,
        underLimit: proMargin.savedMarginPct != null && isBelowMarginLimit(proMargin.savedMarginPct, proMargin.marginLimit),
        data: proMargin.margin,
      },
      marginError: proMargin.error,
      marginLoading: proMargin.loading,
      marginRefresh: proMargin.refresh,
    });
  }, [
    mode,
    onBadgeInfo,
    editing.orderDetails?.moveType,
    editing.orderDetails?.keyAccount,
    editing.orderDetails?.addlInfoDeleted,
    editing.orderDetails?.container,
    editing.orderDetails?.billToName,
    editing.orderDetails?.billTo,
    editing.orderDetails?.customerName,
    editing.orderDetails?.customer,
    numericPro,
    proEta.activeLegnum,
    proEta.byLeg,
    proMargin.margin,
    proMargin.savedMarginPct,
    proMargin.marginLimit,
    proMargin.error,
    proMargin.loading,
    proMargin.refresh,
  ]);

  // Drawer mode: lift the unsaved-changes flag up so the slideover can warn
  // before Master Pro navigation discards staged edits.
  // An uncreated order is dirty by definition — the whole record is unsaved — so
  // the drawer's close guard prompts even before the user touches a field.
  useEffect(() => {
    if (mode !== 'drawer' || !onDirtyChange) return;
    onDirtyChange(createActive || editing.hasChangesIgnoringAutoStagedSend);
  }, [mode, onDirtyChange, createActive, editing.hasChangesIgnoringAutoStagedSend]);

  if (editing.loading && !editing.orderDetails)
    return <div className="p-4 text-slate-600 dark:text-gray-300">Loading {recordLabel} {pronumber}…</div>;
  if (!editing.orderDetails)
    return (
      <div className="p-4 text-slate-600 dark:text-gray-300">
        {/* Create mode hydrates from the seed in an effect, so the very first
            render has no orderDetails yet — never say "not found" there.
            "Not found" is reserved for a REAL 404 — a fetch that failed any
            other way (network, 5xx, gateway timeout) says so and offers a
            retry, instead of telling dispatchers an existing PRO is gone. */}
        {createActive ? (
          'Preparing new order…'
        ) : editing.loadFailed ? (
          (() => {
            const e = editing.loadError;
            const report = [
              'GWI360 PRO load error',
              `Time: ${e?.at ?? new Date().toISOString()}`,
              `PRO: ${pronumber}`,
              `Endpoint: ${e?.url ?? '(unknown)'}`,
              `HTTP status: ${e?.status ?? 'no response received'}`,
              `Error code: ${e?.code ?? '-'}`,
              `Message: ${e?.message ?? '-'}`,
              `Server detail: ${e?.detail ?? '-'}`,
              `Page: ${window.location.href}`,
              `Online: ${navigator.onLine}`,
              `Browser: ${navigator.userAgent}`,
            ].join('\n');
            return (
              <div className="space-y-2">
                <div>
                  Failed to load PRO {pronumber} — the request didn&apos;t get a
                  response from the server. This is usually temporary.{' '}
                  <button
                    type="button"
                    className="text-blue-600 dark:text-blue-400 underline hover:opacity-80"
                    onClick={() => editing.refresh()}
                  >
                    Retry
                  </button>
                </div>
                <div className="text-xs whitespace-pre-wrap select-all rounded border border-slate-200 dark:border-gray-700 bg-slate-50 dark:bg-gray-800 p-2">
                  {report}
                </div>
                <button
                  type="button"
                  className="text-xs px-2 py-1 rounded border border-slate-300 dark:border-gray-600 hover:bg-slate-100 dark:hover:bg-gray-700"
                  onClick={() => {
                    navigator.clipboard
                      ?.writeText(report)
                      .then(() => {
                        setErrorReportCopied(true);
                        setTimeout(() => setErrorReportCopied(false), 2000);
                      })
                      .catch(() => {
                        /* clipboard unavailable — text stays selectable above */
                      });
                  }}
                >
                  {errorReportCopied ? 'Copied ✓' : 'Copy details for support'}
                </button>
              </div>
            );
          })()
        ) : (
          `PRO ${pronumber} not found.`
        )}
      </div>
    );

  // Templates and orders render the same detail. Editability is governed
  // per-field — by canEdit / the leg templateGate (template state) and, for
  // group-restricted fields, per-field PermissionsContainers — so there's no
  // blanket read-only wrapper (a State-3 field like Customer must stay editable
  // on templates, which a blanket accessLevel=1 wrapper would wrongly disable).
  const detail = (
    <ProDetail
      pronumber={pronumber}
      isTemplate={isTemplate}
      canEditTemplate={canEditTemplate}
      orderDetails={editing.orderDetails}
      editing={editing}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      createMode={createActive}
      createStaging={
        createActive && create
          ? {
              stagedDocs: create.stagedDocs,
              onStagedDocsChange: create.onStagedDocsChange,
              stagedNotes: create.stagedNotes,
              onStagedNotesChange: create.onStagedNotesChange,
              templatePro: create.spec.templatepro,
            }
          : undefined
      }
      // Delete is a server write against a persisted record — no delete flow
      // before the order exists (the Deleted field is inert in create mode too).
      onDeleteAttempt={createActive ? undefined : handleDeleteAttempt}
      deleteChecking={deleteChecking}
      // Create mode has no rolling prompt: POST /orders carries no `inputs`
      // channel, and a brand-new booking has nothing to roll.
      onRollingFaultSet={createActive ? undefined : handleRollingFaultSet}
      // Stages the Responsibility prompt's answer for the Change All fan-out. Create
      // mode has no fan-out (and no pro to post the note against yet).
      onPerDiemNoteSaved={
        createActive ? undefined : (note: string) => { perDiemNoteRef.current = note; }
      }
      // Keeps anchorCurrent (the Change All fan-out's "copy the anchor" source) in
      // step with the trace overlay the detail view shows.
      onTracedOverlayChange={setTracedOverlay}
      hideTitleRow={mode === 'drawer'}
      driverValidationSnapshotRef={driverValidationSnapshotRef}
      overrideApprovals={overrideApprovals}
      troubleTicketRefreshNonce={troubleTicketRefreshNonce}
      etaByLeg={proEta.byLeg}
      marginByLeg={proMargin.byLeg}
      marginLimit={proMargin.marginLimit}
      marginLoading={proMargin.loading}
    />
  );

  return (
    <AdminOverrideProvider value={adminOverride}>
    <ChangeAllProvider
      enabled={!!changeAll}
      targetPros={changeAll?.targetPros ?? []}
      anchorPro={changeAll ? pronumber : null}
      // A template anchor draws checkboxes only on the fields its policy currently
      // leaves unlocked, which depends on the SAVED approval state (the same value the
      // Invoicing tab's field gate reads). Every target is re-judged on its own
      // approval server-side, so a mixed set is reported record by record in the review
      // modal rather than part-applied.
      isTemplate={isTemplate}
      isApproved={isTemplateApproved(editing.orderDetails)}
      stateRef={changeAllStateRef}
      onStateChange={handleChangeAllState}
    >
    <div className="relative flex flex-col h-full">
      {/* Loading veil while switching PROs (e.g. Master Pro prev/next): the
          header position updates instantly, so this covers the stale body
          until the new order data lands. `editing.loading` is only true for a
          fresh/navigation fetch — post-save refresh()es don't toggle it, so
          this never flashes on save. */}
      {editing.loading && editing.orderDetails && (
        <div
          className="absolute inset-0 z-20 flex items-center justify-center bg-white/60 dark:bg-gray-900/60"
          aria-live="polite"
          aria-busy="true"
        >
          <div className="flex items-center gap-2 rounded-md border border-slate-200 dark:border-gray-700 bg-panel px-3 py-2 text-sm text-slate-600 dark:text-gray-300 shadow-sm">
            <span className="inline-block h-4 w-4 rounded-full border-2 border-slate-300 dark:border-gray-600 border-t-blue-600 dark:border-t-blue-400 animate-spin" />
            Loading {recordLabel} {pronumber}…
          </div>
        </div>
      )}
      {rejectedTicketChecking && (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/60 dark:bg-gray-900/60" role="status">
          <span className="rounded-md bg-panel px-3 py-2 text-sm shadow-sm">Checking rejection tickets…</span>
        </div>
      )}
      <div inert={rejectedTicketChecking || undefined} className="flex-1 min-h-0 overflow-auto">{detail}</div>

      {mode === 'drawer' && showFooter && (
        <div className="border-t border-slate-200 bg-white dark:border-gray-700 dark:bg-gray-900 px-4 py-2 flex items-center gap-3">
          {footerLeft && <div className="shrink-0">{footerLeft}</div>}
          <div className="flex-1 min-w-0">
            <SaveBar
              editing={editing}
              onSave={() => handleSaveRef.current()}
              onSaveBeforeAction={() => handleSaveRef.current()}
              onClose={onClose}
              busy={deleteChecking || rejectedTicketChecking || createBusy != null}
              busyLabel={createBusy ?? undefined}
              variant={createActive ? 'create' : 'default'}
              createNoun={isTemplate ? 'Template' : 'Order'}
              canSaveForFanoutOnly={canSaveForFanoutOnly}
            />
          </div>
        </div>
      )}

      {showNoteModal && (
        <ReasonNoteModal onClose={handleNoteCancel} onSubmit={onNoteSubmit} />
      )}

      {overridePrompt && (
        <DriverOverrideApprovalModal
          pronumber={numericPro}
          rows={overridePrompt.rows}
          onConfirm={(reason) => overridePrompt.resolve(reason)}
          onCancel={() => overridePrompt.resolve(null)}
        />
      )}

      {chassisConfirm && (
        <WarningConfirmModal
          open
          title="Chassis Provider"
          message={chassisConfirm.message}
          confirmLabel="Save Anyway"
          cancelLabel="Cancel"
          onConfirm={() => chassisConfirm.resolve(true)}
          onCancel={() => chassisConfirm.resolve(false)}
        />
      )}

      {dupeConfirm && (
        <WarningConfirmModal
          open
          title={isTemplate ? 'Possible Duplicate Template' : 'Possible Duplicate Order'}
          message={
            <>
              {/* The two dupe rules match on entirely different things, so the
                  prompt has to say which one fired. v1 shows the matched keys in
                  its dialog — Container + BOL for an order (checkfordupes,
                  lib_editrecord.pl:14180), Move Type / Bill To / Deliver To /
                  Pickup Rate Zone / Quote Number for a template
                  (checkfordupetemplate, :14392) — and flips "Deliver To" to
                  "Pickup From" on INBOUND, which is the leg the match uses. */}
              {isTemplate
                ? `The same Move Type, Bill To, ${
                    String(editing.orderDetails?.moveType ?? '').toUpperCase() === 'INBOUND'
                      ? 'Pickup From' : 'Deliver To'
                  }, Pickup Rate Zone and Quote Number are already on `
                : 'The container / BOL is already on '}
              {dupeConfirm.dupes.length === 1
                ? (isTemplate ? 'template' : 'order')
                : (isTemplate ? 'templates' : 'orders')}{' '}
              {dupeConfirm.dupes.map((d, i) => (
                <React.Fragment key={String(d)}>
                  {i > 0 && ', '}
                  <a
                    href={proDetailPath(d)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-semibold text-blue-600 hover:underline dark:text-blue-400"
                  >
                    {d}
                  </a>
                </React.Fragment>
              ))}
              . Open{' '}
              {dupeConfirm.dupes.length === 1 ? 'it' : 'them'} in a new tab to
              compare, then save this {isTemplate ? 'template' : 'order'} anyway?
            </>
          }
          confirmLabel="Save Anyway"
          cancelLabel="Cancel"
          onConfirm={() => {
            dupeConfirm.resolve(true);
            setDupeConfirm(null);
          }}
          onCancel={() => {
            dupeConfirm.resolve(false);
            setDupeConfirm(null);
          }}
        />
      )}

      {billToRefConfirm && (
        <WarningConfirmModal
          open
          title="Missing Bill To Ref #"
          message="You are missing a Bill to Ref #. Please update if this is required for the account. Save anyway?"
          confirmLabel="Save Anyway"
          confirmHotkey="S"
          cancelLabel="Cancel"
          onConfirm={() => {
            billToRefConfirm.resolve(true);
            setBillToRefConfirm(null);
          }}
          onCancel={() => {
            billToRefConfirm.resolve(false);
            setBillToRefConfirm(null);
          }}
        />
      )}

      {createWarnConfirm && (
        <WarningConfirmModal
          open
          title="Create Order?"
          message={
            <>
              <ul className="list-disc pl-5 space-y-1">
                {createWarnConfirm.warnings.map((w) => (
                  <li key={w}>{w}</li>
                ))}
              </ul>
              <p className="mt-3">Create it anyway?</p>
            </>
          }
          confirmLabel="Create Anyway"
          cancelLabel="Cancel"
          onConfirm={() => {
            createWarnConfirm.resolve(true);
            setCreateWarnConfirm(null);
          }}
          onCancel={() => {
            createWarnConfirm.resolve(false);
            setCreateWarnConfirm(null);
          }}
        />
      )}

      {deleteModal && (
        <DeleteReasonModal
          pronumber={pronumber}
          isTemplate={isTemplate}
          saving={deleteModal.saving}
          onConfirm={handleDeleteConfirm}
          onCancel={handleDeleteCancel}
        />
      )}

      {/* THD cancellation notice. Category is pinned to SHIPMENT CANCELLED — saving the
          ticket is what queues the EDI event to the partner (edi_event_service's
          THD_TICKET_EVENT_MAP), and closing raises nothing. `pronumber` still resolves:
          the deleted order keeps its ordersall row. In Change All mode the editor shows
          its own "Apply to all", which raises the same ticket on every pro in the set
          whose bill-to is in the family; the rest are reported as skipped. */}
      {cancelTicketBillTo !== null && (
        <TroubleTicketEditModal
          pronumber={numericPro}
          billTo={cancelTicketBillTo}
          initialEdits={{ category: 'THD - SHIPMENT CANCELLED' }}
          lockedCategory
          onClose={() => setCancelTicketBillTo(null)}
          onSaved={() => setCancelTicketBillTo(null)}
        />
      )}

      {rollingModal && (
        <RollingReasonModal
          pronumber={pronumber}
          rollingFault={rollingModal.fault}
          saving={rollingModal.saving}
          onConfirm={handleRollingConfirm}
          onCancel={handleRollingCancel}
        />
      )}

      {/* THE fan-out review. One modal per Save covering every flagged arm; its flags are
          cleared here rather than in the Invoicing tab, which may not even be mounted. */}
      {fanout && (
        <ChangeAllFanoutModal
          fanout={fanout}
          isTemplate={isTemplate}
          anchorPro={pronumber}
          onClose={() => {
            setFanout(null);
            // The fan-out consumed the staged per-diem note; a later fan-out on this same
            // pro must not silently re-apply it.
            perDiemNoteRef.current = null;
            changeAllStateRef.current?.setCheckedInvoiceFields(new Set());
            changeAllStateRef.current?.setCheckedInvoiceLines(new Map());
            // A THD delete queued its cancellation prompt behind this modal. Opens on
            // close either way — applying the delete to the set and then declining to
            // notify the partner are independent answers.
            const pendingCancel = pendingCancelBillToRef.current;
            pendingCancelBillToRef.current = null;
            if (pendingCancel !== null) setCancelTicketBillTo(pendingCancel);
          }}
          onApplied={() => {
            // Refetch the anchor so the drawer reflects the just-saved changes.
            editing.refresh?.();
            changeAllStateRef.current?.setCheckedInvoiceFields(new Set());
            changeAllStateRef.current?.setCheckedInvoiceLines(new Map());
          }}
        />
      )}

      {rejectedTicketOpen && editing.orderDetails && (
        <TroubleTicketEditModal
          pronumber={numericPro}
          billTo={editing.orderDetails.billTo ?? undefined}
          initialEdits={{ driverticket: 'Y', category: 'REJECTED' }}
          hasUnsavedOrderChanges={editing.hasChangesIgnoringDispatchFlags}
          onClose={() => {
            // Reached BOTH ways: cancel/X/Esc, and after a successful save (the modal
            // calls onSaved then onClose). The ref is what tells them apart — onSaved has
            // already emptied it, so only an abandoned ticket still has a snapshot here.
            // Restoring wholesale is safe: the modal is a blocking overlay, so nothing
            // else can have been staged since it was taken.
            const snapshot = rejectedRevertRef.current;
            rejectedRevertRef.current = null;
            if (snapshot) editing.revertToSnapshot(snapshot);
            setRejectedTicketOpen(false);
          }}
          onSaved={() => {
            // Ticket exists — the REJECTED keeps its record and stays staged for the
            // order Save. Dropping the snapshot HERE is what makes that stick: onClose
            // runs next and would otherwise revert the very edit the ticket documents.
            //
            // The ticket is already persisted, so abandoning the order from here leaves a
            // REJECTED ticket with no REJECTED leg; that is deliberate (#33683) — it still
            // puts eyes on the pro and can be corrected or closed.
            rejectedRevertRef.current = null;
            setRejectedTicketOpen(false);
            setTroubleTicketRefreshNonce((n) => n + 1);
          }}
          onRescheduleApplied={editing.refresh}
        />
      )}

      {/* Drop=Y → offer LCs the chance to clear appt dates on the legs that
          split out of the dispatch grouping (raised by useOrderEditing from
          either the grid Drop badge or the order-level isDrop toggle). */}
      <WarningConfirmModal
        open={editing.dropApptClearPrompt?.kind === 'confirm'}
        title="Clear Appointment Dates?"
        message={
          <div>
            <p className="mb-2">
              Would you like to clear the appointment dates on the following leg(s)?
            </p>
            <ul className="list-disc space-y-0.5 pl-5">
              {editing.dropApptClearPrompt?.kind === 'confirm' &&
                editing.dropApptClearPrompt.legs.map((l) => (
                  <li key={l.legrecno}>{l.label}</li>
                ))}
            </ul>
          </div>
        }
        confirmLabel="Clear Appt Dates"
        cancelLabel="Keep Appt Date"
        onConfirm={editing.confirmDropApptClear}
        onCancel={editing.dismissDropApptClearPrompt}
      />
      <WarningConfirmModal
        open={confirmOverrideOff > 0}
        title="Discard Admin Override changes?"
        message={
          <div>
            <p className="mb-2">
              Turning Admin Override off locks these fields again, so the{' '}
              {confirmOverrideOff} change{confirmOverrideOff === 1 ? '' : 's'} you made
              while it was on will be undone.
            </p>
            <p>Anything you changed before turning it on is kept.</p>
          </div>
        }
        confirmLabel="Discard and lock"
        cancelLabel="Keep editing"
        onConfirm={discardOverrideEdits}
        onCancel={() => setConfirmOverrideOff(0)}
      />
      <AlertModal
        open={editing.dropApptClearPrompt?.kind === 'blocked'}
        title="Cannot Change Drop/Live"
        message="Cannot change drop/live when a driver is assigned to this leg. Please get with dispatch to update."
        closeLabel="OK"
        onClose={editing.dismissDropApptClearPrompt}
      />
    </div>
    </ChangeAllProvider>
    </AdminOverrideProvider>
  );
};

export default ProDetailContainer;
